import { useParams } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { createFund, fetchFundById, updateFund } from 'api/funds';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Fund } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface FundFormValues {
    name: string;
    code: string;
}

const FundsFormDrawer = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { goBack } = useDialogRoute();
    const { fundId: _fundId } = useParams<{ fundId: string }>();
    const fundId = Number(_fundId);

    const { data: fund } = useSuspenseQuery({
        queryKey: accountQueryKeys.fundById(accountId, fundId),
        queryFn: () =>
            conditionallyFetchOrNull(() => fetchFundById(accountId, fundId), !isNaN(fundId)),
    });

    const defaultValues = {
        name: fund?.name || '',
        code: fund?.code || '',
    };
    const methods = Form.useGivebutterForm<FundFormValues>({ defaultValues });
    const {
        control,
        formState: { isDirty, isValid },
        path,
        handleSubmit,
        setError,
        resetFormButton,
    } = methods;

    const { mutate: submit, isPending: isSubmitting } = useMutation<
        Fund,
        GivebutterAxiosError,
        FundFormValues
    >({
        mutationFn: values =>
            fund ? updateFund(accountId, fund.id, values) : createFund(accountId, values),
        onSuccess: () => {
            success(`Fund ${fund ? 'updated' : 'created'} successfully!`);
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.funds(accountId) });
            goBack();
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
            const errors = e.response?.data.errors;

            if (errors) {
                for (const field in errors) {
                    setError(field as keyof FundFormValues, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form className="contents" onSubmit={handleSubmit(values => submit(values))}>
                <Drawer.Header>
                    <Drawer.Title>{fund ? 'Update' : 'Add'} Fund</Drawer.Title>
                </Drawer.Header>
                <Drawer.Body className="flex flex-col gap-4">
                    <Form.Container>
                        <Form.Label name={path('name')}>
                            Name
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name="name"
                            rules={{ required: 'Please enter a Name' }}
                        >
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('name')} />
                    </Form.Container>

                    <Form.Container>
                        <Form.Label name={path('code')}>Code</Form.Label>
                        <Form.TextInputRoot control={control} name="code">
                            <Form.TextInputForm />
                        </Form.TextInputRoot>
                        <Form.Error name={path('code')} />
                    </Form.Container>
                </Drawer.Body>
                <Drawer.Footer>
                    <div className="flex w-full flex-wrap-reverse items-center justify-between gap-2">
                        <Drawer.Cancel asChild={true}>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Drawer.Cancel>

                        <Form.SubmitButton
                            disabled={isSubmitting || !isDirty || !isValid}
                            isSubmitting={isSubmitting}
                        >
                            {fund ? 'Update Fund' : 'Add Fund'}
                        </Form.SubmitButton>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.GivebutterFormProvider>
    );
};

export { FundsFormDrawer };
